import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
    faCircleF as RegularCircleF,
    faCircleM as RegularCircleM,
    faCircleS as RegularCircleS,
    faCircleT as RegularCircleT,
    faCircleW as RegularCircleW,
} from "@fortawesome/pro-regular-svg-icons";
import {
    faCircleF as SolidCircleF,
    faCircleM as SolidCircleM,
    faCircleS as SolidCircleS,
    faCircleT as SolidCircleT,
    faCircleW as SolidCircleW,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { RRule } from "rrule";
import Helper from "../Helper";
import { contiunousSequenceFromStartAndEnd, rRuleDaysToDayNums } from "../components/Calendar/helper";
import { upcomingClosureDaysForProject } from "../components/Helper/Calendar";
import { RepeatingCalendarEventDefinition } from "../models/RepeatingCalendarEventDefinition";

interface Props {
    project: any;
    iconSize?: "1x" | "2x" | "3x" | "4x" | "5x";
    headerSize?: "text-xs" | "text-sm" | "text-base" | "text-lg" | "text-xl" | "text-2xl" | "text-3xl";
    headerColor?: string;
    primaryColor?: string;
    secondaryColor?: string;
    showNextClosure?: boolean;
}

const JobsiteHours: React.FC<Props> = ({
    project,
    iconSize = "1x",
    headerSize = "text-base",
    headerColor = "text-white",
    primaryColor = "#ff9e00",
    secondaryColor = "#808080",
    showNextClosure = true,
    // eslint-disable-next-line complexity
}) => {
    const [closedDaysIndex, setClosedDaysIndex] = React.useState<number[]>([]);
    const [nextClosure, setNextClosure] = React.useState<any | null>(null);

    const openHoursDefinition = useMemo(() => {
        const repeatingCalendarEventDefinitions = project?.repeating_calendar_event_definitions || [];

        const def = repeatingCalendarEventDefinitions.find((def: any) => def.name === "Project Hours");

        if (def) {
            const newDef = RepeatingCalendarEventDefinition.fromApiObject(def, project.timezone);

            return newDef;
        }

        return null;
    }, [project]);

    const deliveryHoursAllDay = useMemo(() => {
        if (!openHoursDefinition) {
            return false;
        }

        // If the hours are multiples of 24 hours, then we know they are all day
        if ((openHoursDefinition.duration / 60) % 24 === 0) {
            return true;
        }

        return false;
    }, [openHoursDefinition]);

    const deliveryHoursStart = useMemo(() => {
        if (!openHoursDefinition) {
            return null;
        }

        return openHoursDefinition.startAt || dayjs();
    }, [openHoursDefinition]);

    const deliveryHoursEnd = useMemo(() => {
        if (!openHoursDefinition) {
            return null;
        }

        return dayjs(deliveryHoursStart).add(openHoursDefinition.duration, "minute");
    }, [openHoursDefinition, deliveryHoursStart]);

    React.useEffect(() => {
        if (!project) {
            return;
        }

        getUpcomingClosureDays(project);
    }, [project]);

    const workWeekDays = useMemo(() => {
        if (!openHoursDefinition) {
            return [];
        }

        if (deliveryHoursAllDay) {
            const startDay = openHoursDefinition.startAt?.get("day");

            if (startDay === undefined) {
                return [];
            }

            const endDay = (startDay + openHoursDefinition.duration / (24 * 60) - 1) % 7;

            return contiunousSequenceFromStartAndEnd(startDay, endDay).filter((day) => !closedDaysIndex.includes(day));
        }

        const rrule = RRule.fromString(openHoursDefinition?.rrule);

        return rRuleDaysToDayNums(rrule.options.byweekday as number[]).filter((day) => !closedDaysIndex.includes(day));
    }, [project, deliveryHoursAllDay, openHoursDefinition, closedDaysIndex]);

    const getUpcomingClosureDays = async (project: any) => {
        if (!project) {
            return [];
        }

        const upcomingExceptionDays = await upcomingClosureDaysForProject(project);

        setClosedDaysIndex([...upcomingExceptionDays.closedDaysIndex]);
        setNextClosure(upcomingExceptionDays.nextClosureDay);
    };

    return (
        <div className="flex flex-col text-center justify-center h-full">
            <div className="flex flex-row justify-between w-full">
                <div
                    className={Helper.classNames(
                        "font-plot-open-sans font-semibold uppercase whitespace-nowrap mr-4",
                        headerSize,
                        headerColor
                    )}
                >
                    Delivery Hours
                </div>
                <div
                    className={Helper.classNames(
                        "font-plot-open-sans  font-semibold uppercase whitespace-nowrap",
                        headerSize,
                        headerColor
                    )}
                >
                    {deliveryHoursAllDay ? (
                        <span className="text-plot-orange">All Day</span>
                    ) : (
                        <>
                            {deliveryHoursStart?.format("hh:mm A")} - {deliveryHoursEnd?.format("hh:mm A")}
                        </>
                    )}
                </div>
            </div>
            <div className="flex flex-row justify-evenly my-2">
                {/* 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday, 4 = Thursday, 5 = Friday, 6 = Saturday */}
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(0) ? SolidCircleS : RegularCircleS) as IconProp}
                    color={workWeekDays.includes(0) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(1) ? SolidCircleM : RegularCircleM) as IconProp}
                    color={workWeekDays.includes(1) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(2) ? SolidCircleT : RegularCircleT) as IconProp}
                    color={workWeekDays.includes(2) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(3) ? SolidCircleW : RegularCircleW) as IconProp}
                    color={workWeekDays.includes(3) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(4) ? SolidCircleT : RegularCircleT) as IconProp}
                    color={workWeekDays.includes(4) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(5) ? SolidCircleF : RegularCircleF) as IconProp}
                    color={workWeekDays.includes(5) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
                <FontAwesomeIcon
                    icon={(workWeekDays.includes(6) ? SolidCircleS : RegularCircleS) as IconProp}
                    color={workWeekDays.includes(6) ? primaryColor : secondaryColor}
                    size={iconSize}
                />
            </div>
            {showNextClosure && nextClosure && (
                <div
                    className={Helper.classNames(
                        "flex flex-row justify-center items-center gap-2 w-full text-xl print:text-sm",
                        headerColor
                    )}
                >
                    <p>Next Closure:</p>
                    <p>{nextClosure.name}</p>
                    <p>{dayjs(nextClosure.date).tz(project.timezone).format("MMM D")}</p>
                </div>
            )}
        </div>
    );
};

export default JobsiteHours;
